/**
 * Guided Case Intelligence — one model, three renderings.
 *
 * Housing is a months-long process with several actors, and the biggest UX
 * failure of the first build was that no screen reliably told a person where
 * they were or what happened next. Every housing surface now answers the same
 * five questions in the same order:
 *
 *   1. Where am I?              → stage
 *   2. What happens next?       → next (with the actor who owns it)
 *   3. What is blocking?        → blockers
 *   4. Why did it decide that?  → reasoning
 *   5. Where is the proof?      → proof
 *
 * Apps compute a CaseGuidance with pure selectors over projections they already
 * fetch, then pick a rendering: GuidanceRail (portal sticky rail), GuidanceBand
 * (console hero strip) or GuidanceSheet (mobile collapsible).
 */
import type { ReactNode } from 'react';
import { useState } from 'react';
import { useI18n, type Localized } from './i18n.js';
import { AiPanel, ProvenanceChip, type ProvenanceKind } from './intelligence.js';

function cx(...parts: (string | false | null | undefined)[]): string {
  return parts.filter(Boolean).join(' ');
}

// ---------------------------------------------------------------------------
// The contract
// ---------------------------------------------------------------------------

/** Who the process is waiting on right now. Drives colour and wording. */
export type GuidancePosture =
  | 'action_needed'
  | 'awaiting_ministry'
  | 'awaiting_partner'
  | 'blocked'
  | 'complete';

export interface GuidanceStage {
  id: string;
  label: Localized;
  /** 1-based position for display. */
  index: number;
  total: number;
  /** ISO timestamp the case entered this stage, when known. */
  since?: string | null;
}

export interface GuidanceAction {
  title: Localized;
  body: Localized;
  cta: Localized;
  href?: string;
  onAction?: () => void;
  /** The person or team who owns this step — named, never implied. */
  actor: Localized;
  /** A realistic wait, only when the programme can honestly state one. */
  estimate?: Localized | null;
  disabled?: boolean;
}

export interface GuidanceBlocker {
  id: string;
  label: Localized;
  detail?: Localized;
  href?: string;
}

export interface GuidanceReasoning {
  title: Localized;
  bullets?: Localized[];
  provenance: ProvenanceKind;
  model?: string | null;
  body?: ReactNode;
}

export interface GuidanceProof {
  id: string;
  label: Localized;
  kind: 'pdf' | 'record';
  href?: string;
  onOpen?: () => void;
  busy?: boolean;
}

export interface CaseGuidance {
  stage: GuidanceStage;
  posture: GuidancePosture;
  next: GuidanceAction | null;
  blockers: GuidanceBlocker[];
  reasoning: GuidanceReasoning[];
  proof: GuidanceProof[];
}

// ---------------------------------------------------------------------------
// Posture vocabulary
// ---------------------------------------------------------------------------

const POSTURE_LABEL: Record<GuidancePosture, Localized> = {
  action_needed: { en: 'Your turn', ar: 'دورك الآن' },
  awaiting_ministry: { en: 'With the Ministry', ar: 'عند الوزارة' },
  awaiting_partner: { en: 'With your partner', ar: 'عند الجهة الشريكة' },
  blocked: { en: 'Blocked', ar: 'متوقف' },
  complete: { en: 'Complete', ar: 'مكتمل' },
};

const POSTURE_CHIP: Record<GuidancePosture, string> = {
  action_needed: 'border-aegold-500/45 bg-aegold-100 text-gold-deep',
  awaiting_ministry: 'border-seablue-600/35 bg-seablue-50 text-seablue-800',
  awaiting_partner: 'border-seablue-600/35 bg-seablue-50 text-seablue-800',
  blocked: 'border-aered-600/40 bg-aered-50 text-reject',
  complete: 'border-aegreen-600/35 bg-aegreen-50 text-approve',
};

const POSTURE_BAR: Record<GuidancePosture, string> = {
  action_needed: 'bg-aegold-600',
  awaiting_ministry: 'bg-seablue-700',
  awaiting_partner: 'bg-seablue-700',
  blocked: 'bg-aered-600',
  complete: 'bg-aegreen-600',
};

export function PosturePill({
  posture,
  className,
}: {
  posture: GuidancePosture;
  className?: string;
}) {
  const { l } = useI18n();
  return (
    <span
      className={cx(
        'inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-[10px] font-black tracking-wide',
        POSTURE_CHIP[posture],
        className,
      )}
    >
      <span aria-hidden className="inline-block h-1.5 w-1.5 rounded-full bg-current" />
      {l(POSTURE_LABEL[posture])}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Shared internals
// ---------------------------------------------------------------------------

function StageMeter({ stage, posture }: { stage: GuidanceStage; posture: GuidancePosture }) {
  const { l } = useI18n();
  const pct = stage.total === 0 ? 0 : Math.round((stage.index / stage.total) * 100);
  return (
    <div className="min-w-0">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <span className="min-w-0 truncate text-xs font-black text-ink">{l(stage.label)}</span>
        <span className="shrink-0 font-mono text-[11px] font-black tabular-nums text-ink-faint" dir="ltr">
          {stage.index}/{stage.total}
        </span>
      </div>
      <div
        className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-aeblack-100"
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={stage.total}
        aria-valuenow={stage.index}
        aria-label={l({ en: 'Journey progress', ar: 'تقدم الرحلة' })}
      >
        <div className={cx('h-full rounded-full', POSTURE_BAR[posture])} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function NextActionBody({ next, compact }: { next: GuidanceAction; compact?: boolean }) {
  const { l } = useI18n();
  return (
    <>
      <div className={cx('font-black tracking-tight text-ink', compact ? 'text-sm' : 'text-base')}>
        {l(next.title)}
      </div>
      <p className="mt-1 text-xs leading-6 text-ink-soft">{l(next.body)}</p>
      <div className="mt-2.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] font-bold">
        <span className="inline-flex items-center gap-1.5 text-ink-soft">
          <svg
            width="12"
            height="12"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden
          >
            <path d="M20 21a8 8 0 1 0-16 0" />
            <circle cx="12" cy="7" r="4" />
          </svg>
          {l(next.actor)}
        </span>
        {next.estimate ? (
          <span className="inline-flex items-center gap-1.5 text-ink-faint">
            <svg
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden
            >
              <circle cx="12" cy="12" r="9" />
              <path d="M12 7v5l3 2" />
            </svg>
            {l(next.estimate)}
          </span>
        ) : null}
      </div>
    </>
  );
}

function ActionButton({ next, className }: { next: GuidanceAction; className?: string }) {
  const { l } = useI18n();
  const label = l(next.cta);
  if (next.href && !next.disabled) {
    return (
      <a href={next.href} className={className}>
        {label}
      </a>
    );
  }
  return (
    <button type="button" onClick={next.onAction} disabled={next.disabled} className={className}>
      {label}
    </button>
  );
}

function BlockerList({ blockers, compact }: { blockers: GuidanceBlocker[]; compact?: boolean }) {
  const { l } = useI18n();
  if (blockers.length === 0) return null;
  return (
    <div className="min-w-0">
      <div className="text-[10px] font-black uppercase tracking-wider text-camel-700">
        {l({ en: 'Blocking', ar: 'يعيق التقدم' })}
      </div>
      <ul className={cx('mt-1.5', compact ? 'space-y-1' : 'space-y-1.5')}>
        {blockers.map((blocker) => (
          <li key={blocker.id} className="flex gap-2 text-[11px] leading-5">
            <span aria-hidden className="mt-1.5 inline-block h-1 w-1 shrink-0 rounded-full bg-camel-600" />
            <span className="min-w-0">
              {blocker.href ? (
                <a href={blocker.href} className="font-bold text-seablue-800 underline underline-offset-2">
                  {l(blocker.label)}
                </a>
              ) : (
                <span className="font-bold text-ink">{l(blocker.label)}</span>
              )}
              {blocker.detail ? <span className="text-ink-soft"> — {l(blocker.detail)}</span> : null}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function ProofList({ proof }: { proof: GuidanceProof[] }) {
  const { l } = useI18n();
  if (proof.length === 0) return null;
  return (
    <div className="min-w-0">
      <div className="text-[10px] font-black uppercase tracking-wider text-ink-faint">
        {l({ en: 'Proof', ar: 'المستندات' })}
      </div>
      <ul className="mt-1.5 flex flex-wrap gap-1.5">
        {proof.map((item) => {
          const label = l(item.label);
          const shell =
            'inline-flex min-h-9 max-w-full items-center gap-1.5 rounded-lg border border-aeblack-100 bg-white px-2.5 text-[11px] font-bold text-ink disabled:opacity-50';
          const icon = (
            <svg
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden
              className="shrink-0 text-ink-faint"
            >
              {item.kind === 'pdf' ? (
                <>
                  <path d="M14 3v5h5" />
                  <path d="M19 21H5V3h9l5 5z" />
                </>
              ) : (
                <>
                  <path d="M4 6h16M4 12h16M4 18h10" />
                </>
              )}
            </svg>
          );
          return (
            <li key={item.id} className="min-w-0">
              {item.href ? (
                <a href={item.href} className={shell}>
                  {icon}
                  <span className="truncate">{label}</span>
                </a>
              ) : (
                <button type="button" onClick={item.onOpen} disabled={item.busy} className={shell}>
                  {icon}
                  <span className="truncate">{label}</span>
                </button>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function ReasoningList({ reasoning }: { reasoning: GuidanceReasoning[] }) {
  if (reasoning.length === 0) return null;
  return (
    <div className="space-y-2.5">
      {reasoning.map((item, index) => (
        <AiPanel
          key={index}
          title={item.title}
          bullets={item.bullets}
          provenance={item.provenance}
          model={item.model}
          tone="quiet"
        >
          {item.body}
        </AiPanel>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Rendering 1 — GuidanceRail (citizen portal, sticky right rail)
// ---------------------------------------------------------------------------

/**
 * The editorial rail. Sits beside the canvas on wide screens and carries the
 * whole guidance model top to bottom, in the order the five questions are asked.
 * `extras` lets a screen inject its own panels (eligibility verdict, entitlement)
 * between the next action and the reasoning.
 */
export function GuidanceRail({
  guidance,
  extras,
  footer,
  label,
  className,
}: {
  guidance: CaseGuidance;
  extras?: ReactNode;
  footer?: ReactNode;
  label?: Localized;
  className?: string;
}) {
  const { l } = useI18n();
  const { stage, posture, next, blockers, reasoning, proof } = guidance;

  return (
    <aside
      aria-label={l(label ?? { en: 'What happens next', ar: 'ما التالي' })}
      data-testid="guidance-rail"
      className={cx('space-y-3', className)}
    >
      <div className="rounded-2xl border border-aegold-500/30 bg-white/90 p-4 sm:p-5">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <PosturePill posture={posture} />
          {stage.since ? (
            <span className="text-[10px] font-bold text-ink-faint" dir="auto">
              {l({ en: 'since', ar: 'منذ' })} {relativeDay(stage.since, l)}
            </span>
          ) : null}
        </div>

        <div className="mt-3.5">
          <StageMeter stage={stage} posture={posture} />
        </div>

        {next ? (
          <div className="mt-4 border-t border-aegold-500/25 pt-3.5">
            <NextActionBody next={next} />
            <ActionButton next={next} className="housing-button-primary mt-3.5 w-full text-sm" />
          </div>
        ) : null}

        {blockers.length > 0 ? (
          <div className="mt-4 border-t border-aegold-500/25 pt-3.5">
            <BlockerList blockers={blockers} />
          </div>
        ) : null}
      </div>

      {extras}

      <ReasoningList reasoning={reasoning} />

      {proof.length > 0 ? (
        <div className="rounded-2xl border border-aeblack-100 bg-white/90 p-4">
          <ProofList proof={proof} />
        </div>
      ) : null}

      {footer}
    </aside>
  );
}

// ---------------------------------------------------------------------------
// Rendering 2 — GuidanceBand (staff console, horizontal strip under the hero)
// ---------------------------------------------------------------------------

/**
 * The console strip. Officers work several cases a day, so this trades the
 * rail's vertical storytelling for a dense horizontal read: posture, stage,
 * owner, what is blocking, and the one action this role owns.
 */
export function GuidanceBand({
  guidance,
  actionClassName = 'aegov-btn btn-sm min-h-10 inline-flex items-center justify-center',
  className,
}: {
  guidance: CaseGuidance;
  actionClassName?: string;
  className?: string;
}) {
  const { l } = useI18n();
  const { stage, posture, next, blockers } = guidance;

  return (
    <section
      aria-label={l({ en: 'Case guidance', ar: 'إرشاد الحالة' })}
      data-testid="guidance-band"
      className={cx(
        'mb-4 rounded-xl border border-aeblack-100 bg-white p-4',
        className,
      )}
    >
      <div className="grid gap-4 lg:grid-cols-[minmax(0,14rem)_minmax(0,1fr)_auto] lg:items-center">
        <div className="min-w-0">
          <PosturePill posture={posture} />
          <div className="mt-2.5">
            <StageMeter stage={stage} posture={posture} />
          </div>
        </div>

        <div className="min-w-0">
          {next ? <NextActionBody next={next} compact /> : null}
          {blockers.length > 0 ? (
            <div className="mt-2.5">
              <BlockerList blockers={blockers} compact />
            </div>
          ) : null}
        </div>

        {next ? (
          <div className="lg:justify-self-end">
            <ActionButton next={next} className={actionClassName} />
          </div>
        ) : null}
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Rendering 3 — GuidanceSheet (narrow viewports)
// ---------------------------------------------------------------------------

/**
 * Below the rail breakpoint the guidance becomes a collapsible summary pinned
 * above the content: posture and next action always visible, the rest one tap
 * away. Never a floating overlay — it must not cover the form it describes.
 */
export function GuidanceSheet({
  guidance,
  extras,
  className,
}: {
  guidance: CaseGuidance;
  extras?: ReactNode;
  className?: string;
}) {
  const { l } = useI18n();
  const [open, setOpen] = useState(false);
  const { stage, posture, next, blockers, reasoning, proof } = guidance;
  const hasMore = blockers.length > 0 || reasoning.length > 0 || proof.length > 0 || Boolean(extras);

  return (
    <section
      aria-label={l({ en: 'What happens next', ar: 'ما التالي' })}
      data-testid="guidance-sheet"
      className={cx('rounded-2xl border border-aegold-500/30 bg-white/95 p-4', className)}
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <PosturePill posture={posture} />
        <span className="font-mono text-[11px] font-black tabular-nums text-ink-faint" dir="ltr">
          {stage.index}/{stage.total}
        </span>
      </div>

      <div className="mt-3">
        <StageMeter stage={stage} posture={posture} />
      </div>

      {next ? (
        <div className="mt-3.5 border-t border-aegold-500/25 pt-3">
          <NextActionBody next={next} compact />
          <ActionButton next={next} className="housing-button-primary mt-3 w-full text-sm" />
        </div>
      ) : null}

      {hasMore ? (
        <>
          <button
            type="button"
            onClick={() => setOpen((value) => !value)}
            aria-expanded={open}
            className="mt-3 inline-flex cursor-pointer items-center gap-1.5 text-[11px] font-black text-seablue-800"
          >
            {open
              ? l({ en: 'Hide details', ar: 'إخفاء التفاصيل' })
              : l({ en: 'Why, blockers and documents', ar: 'الأسباب والمعيقات والمستندات' })}
            <svg
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.4"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden
              className={open ? 'rotate-180' : undefined}
            >
              <path d="M6 9l6 6 6-6" />
            </svg>
          </button>

          {open ? (
            <div className="mt-3 space-y-3 border-t border-aegold-500/25 pt-3">
              <BlockerList blockers={blockers} compact />
              {extras}
              <ReasoningList reasoning={reasoning} />
              <ProofList proof={proof} />
            </div>
          ) : null}
        </>
      ) : null}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Stage rail — the shared journey timeline both apps render
// ---------------------------------------------------------------------------

export interface StageRailItem {
  id: string;
  label: Localized;
  status: 'pending' | 'in_progress' | 'completed' | 'failed';
  /** Who owns this stage, shown so nobody wonders who to chase. */
  actor?: Localized | null;
  at?: string | null;
  notes?: Localized | null;
  /** Plain-language explanation of the real ministry step, in a disclosure. */
  explainer?: Localized | null;
  /** Nested items, used for the two field-research visits under Review Research. */
  children?: StageRailItem[];
  /** Extra content rendered under the stage body (decision cards, meters). */
  body?: ReactNode;
}

const STAGE_DOT: Record<StageRailItem['status'], string> = {
  completed: 'border-aegreen-600 bg-aegreen-600 text-white',
  in_progress: 'border-aegold-600 bg-aegold-100 text-gold-deep',
  failed: 'border-aered-600 bg-aered-600 text-white',
  pending: 'border-aeblack-200 bg-white text-ink-faint',
};

const STAGE_STATUS_LABEL: Record<StageRailItem['status'], Localized> = {
  completed: { en: 'Done', ar: 'منجز' },
  in_progress: { en: 'In progress', ar: 'قيد التنفيذ' },
  failed: { en: 'Not approved', ar: 'غير موافق عليه' },
  pending: { en: 'Not started', ar: 'لم يبدأ' },
};

/**
 * A real ordered list with `aria-current="step"`, used for the five-stage
 * pre-approval pipeline, the financing-contract stages and the visit lifecycle.
 * Replaces three different hand-rolled numbered lists.
 */
export function StageRail({
  items,
  className,
  nested,
}: {
  items: StageRailItem[];
  className?: string;
  nested?: boolean;
}) {
  const { l, lang } = useI18n();

  return (
    <ol className={cx(nested ? 'space-y-2.5' : 'space-y-3', className)}>
      {items.map((item, index) => (
        <li
          key={item.id}
          aria-current={item.status === 'in_progress' ? 'step' : undefined}
          data-stage-id={item.id}
          data-stage-status={item.status}
          className="relative flex gap-3"
        >
          <div className="flex flex-col items-center">
            <span
              aria-hidden
              className={cx(
                'grid shrink-0 place-items-center rounded-full border-2 text-[10px] font-black',
                nested ? 'h-5 w-5' : 'h-7 w-7',
                STAGE_DOT[item.status],
              )}
            >
              {item.status === 'completed' ? (
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M5 13l4 4L19 7" />
                </svg>
              ) : item.status === 'failed' ? (
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.2" strokeLinecap="round">
                  <path d="M6 6l12 12M18 6L6 18" />
                </svg>
              ) : (
                index + 1
              )}
            </span>
            {index < items.length - 1 ? (
              <span
                aria-hidden
                className={cx(
                  'mt-1 w-0.5 flex-1',
                  item.status === 'completed' ? 'bg-aegreen-600/35' : 'bg-aeblack-100',
                )}
              />
            ) : null}
          </div>

          <div className={cx('min-w-0 flex-1', index < items.length - 1 && 'pb-1')}>
            <div className="flex flex-wrap items-baseline gap-x-2.5 gap-y-1">
              <span className={cx('font-black text-ink', nested ? 'text-xs' : 'text-sm')}>
                {l(item.label)}
              </span>
              <span className="text-[10px] font-black uppercase tracking-wider text-ink-faint">
                {l(STAGE_STATUS_LABEL[item.status])}
              </span>
              {item.at ? (
                <span className="font-mono text-[10px] font-bold tabular-nums text-ink-faint" dir="ltr">
                  {formatStageDate(item.at, lang)}
                </span>
              ) : null}
            </div>

            {item.actor ? (
              <div className="mt-0.5 text-[11px] font-bold text-ink-soft">{l(item.actor)}</div>
            ) : null}

            {item.notes ? (
              <p className="mt-1 text-xs leading-6 text-ink-soft">{l(item.notes)}</p>
            ) : null}

            {item.body ? <div className="mt-2">{item.body}</div> : null}

            {item.children && item.children.length > 0 ? (
              <div className="mt-2.5 rounded-lg border border-aeblack-100 bg-aeblack-50/50 p-3">
                <StageRail items={item.children} nested />
              </div>
            ) : null}

            {item.explainer ? (
              <details className="mt-1.5">
                <summary className="cursor-pointer text-[11px] font-black text-seablue-800">
                  {l({ en: 'What happens here', ar: 'ما يحدث في هذه المرحلة' })}
                </summary>
                <p className="mt-1 text-[11px] leading-5 text-ink-soft">{l(item.explainer)}</p>
              </details>
            ) : null}
          </div>
        </li>
      ))}
    </ol>
  );
}

// ---------------------------------------------------------------------------
// CoachCard — the per-chapter guidance used inside the intake form
// ---------------------------------------------------------------------------

/**
 * Explains a chapter before the citizen fills it: why it matters, what it
 * unlocks, and how much is left. Without this the intake is 13 fields and 13
 * conditional documents with no narrative.
 */
export function CoachCard({
  eyebrow,
  title,
  body,
  unlocks,
  done,
  total,
  provenance,
  className,
}: {
  eyebrow?: Localized;
  title: Localized;
  body: Localized;
  unlocks?: Localized | null;
  done?: number;
  total?: number;
  provenance?: ProvenanceKind;
  className?: string;
}) {
  const { l } = useI18n();
  const remaining = total != null && done != null ? Math.max(0, total - done) : null;

  return (
    <div className={cx('rounded-xl border border-aegold-500/30 bg-aegold-50/70 p-4', className)}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          {eyebrow ? (
            <div className="text-[10px] font-black uppercase tracking-wider text-gold-deep">
              {l(eyebrow)}
            </div>
          ) : null}
          <h3 className="mt-0.5 text-sm font-black tracking-tight text-ink">{l(title)}</h3>
        </div>
        {provenance ? <ProvenanceChip kind={provenance} /> : null}
      </div>

      <p className="mt-2 text-xs leading-6 text-ink-soft">{l(body)}</p>

      {unlocks ? (
        <p className="mt-2 flex gap-2 text-[11px] leading-5 font-semibold text-seablue-800">
          <svg
            width="13"
            height="13"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden
            className="mt-0.5 shrink-0"
          >
            <path d="M5 12h14M13 6l6 6-6 6" />
          </svg>
          <span className="min-w-0">{l(unlocks)}</span>
        </p>
      ) : null}

      {remaining != null && total != null && total > 0 ? (
        <div className="mt-3 flex items-center gap-2.5">
          <div
            className="h-1.5 flex-1 overflow-hidden rounded-full bg-white"
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={total}
            aria-valuenow={done ?? 0}
            aria-label={l(title)}
          >
            <div
              className="h-full rounded-full bg-aegold-600"
              style={{ width: `${Math.round(((done ?? 0) / total) * 100)}%` }}
            />
          </div>
          <span className="shrink-0 text-[10px] font-black text-ink-faint">
            {remaining === 0
              ? l({ en: 'Complete', ar: 'مكتمل' })
              : l({
                  en: `${remaining} left`,
                  ar: `${remaining} متبقٍ`,
                })}
          </span>
        </div>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatStageDate(iso: string, lang: 'en' | 'ar'): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return new Intl.DateTimeFormat(lang === 'ar' ? 'ar-AE' : 'en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    numberingSystem: 'latn',
  }).format(date);
}

function relativeDay(iso: string, l: (value: Localized) => string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const days = Math.max(0, Math.floor((Date.now() - date.getTime()) / 86_400_000));
  if (days === 0) return l({ en: 'today', ar: 'اليوم' });
  if (days === 1) return l({ en: 'yesterday', ar: 'أمس' });
  return l({ en: `${days} days`, ar: `${days} يوماً` });
}

/** Days remaining against a deadline, for the 90-day loan bank-file window. */
export function daysRemaining(fromIso: string | null | undefined, windowDays: number): number | null {
  if (!fromIso) return null;
  const start = new Date(fromIso);
  if (Number.isNaN(start.getTime())) return null;
  const deadline = start.getTime() + windowDays * 86_400_000;
  return Math.ceil((deadline - Date.now()) / 86_400_000);
}
